import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { getBranding, updateBranding, uploadBrandingLogo, getSystemTexts, updateSystemTexts } from '../../lib/api';
import { Loader2, Upload, Palette, Type, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BrandingSettings = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');
  
  // Branding state
  const [branding, setBranding] = useState({
    app_name: 'UN-IT Forms',
    app_logo_light: null,
    app_logo_dark: null,
    favicon: null,
    primary_color: '#1E40AF',
    secondary_color: '#3B82F6'
  });
  
  // System texts state
  const [texts, setTexts] = useState({
    submit_success_de: '',
    submit_success_tr: '',
    approved_de: '',
    approved_tr: '',
    rejected_de: '',
    rejected_tr: '',
    completed_de: '',
    completed_tr: '',
    error_generic_de: '',
    error_generic_tr: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [brandingData, textsData] = await Promise.all([
        getBranding(),
        getSystemTexts()
      ]);
      setBranding(brandingData);
      setTexts(textsData);
    } catch (error) {
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleBrandingSave = async () => {
    setSaving(true);
    try {
      await updateBranding(branding);
      toast.success('Branding gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleTextsSave = async () => {
    setSaving(true);
    try {
      await updateSystemTexts(texts);
      toast.success('Texte gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e, logoType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const result = await uploadBrandingLogo(file, logoType);
      setBranding(prev => ({
        ...prev,
        [result.field]: result.url
      }));
      toast.success('Logo hochgeladen');
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    }
  };

  const getLogoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };

  // Preview text with placeholder replacement
  const previewText = (text) => {
    return text
      .replace('{{form_name}}', 'Beispielformular')
      .replace('{{department}}', 'Hauptabteilung')
      .replace('{{submitted_at}}', new Date().toLocaleDateString('de-DE'))
      .replace('{{status}}', 'Genehmigt');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Branding & Texte</h1>
        <p className="text-gray-500 mt-1">Passen Sie das Erscheinungsbild und alle Systemtexte an</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="branding" className="flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="texts" className="flex items-center gap-2">
            <Type className="h-4 w-4" />
            Systemtexte
          </TabsTrigger>
        </TabsList>

        {/* BRANDING TAB */}
        <TabsContent value="branding" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Anwendungsname & Logos</CardTitle>
              <CardDescription>
                Diese Einstellungen werden überall im System angezeigt
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Anwendungsname</Label>
                <Input
                  value={branding.app_name}
                  onChange={(e) => setBranding(prev => ({ ...prev, app_name: e.target.value }))}
                  placeholder="UN-IT Forms"
                  data-testid="branding-app-name"
                />
                <p className="text-xs text-gray-500 mt-1">Wird im Header, Login und öffentlichen Seiten angezeigt</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Light Logo */}
                <div className="space-y-2">
                  <Label>Logo (Hell)</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center bg-white">
                    {branding.app_logo_light ? (
                      <img 
                        src={getLogoUrl(branding.app_logo_light)} 
                        alt="Logo Light" 
                        className="max-h-16 mx-auto mb-2"
                      />
                    ) : (
                      <div className="h-16 flex items-center justify-center text-gray-400">
                        Kein Logo
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg,.svg"
                        className="hidden"
                        onChange={(e) => handleLogoUpload(e, 'light')}
                      />
                      <Button variant="outline" size="sm" asChild>
                        <span><Upload className="h-4 w-4 mr-1" /> Hochladen</span>
                      </Button>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">Für helle Hintergründe</p>
                </div>

                {/* Dark Logo */}
                <div className="space-y-2">
                  <Label>Logo (Dunkel)</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center bg-slate-800">
                    {branding.app_logo_dark ? (
                      <img 
                        src={getLogoUrl(branding.app_logo_dark)} 
                        alt="Logo Dark" 
                        className="max-h-16 mx-auto mb-2"
                      />
                    ) : (
                      <div className="h-16 flex items-center justify-center text-gray-400">
                        Kein Logo
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".png,.jpg,.jpeg,.svg"
                        className="hidden"
                        onChange={(e) => handleLogoUpload(e, 'dark')}
                      />
                      <Button variant="outline" size="sm" asChild>
                        <span><Upload className="h-4 w-4 mr-1" /> Hochladen</span>
                      </Button>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">Für dunkle Hintergründe</p>
                </div>

                {/* Favicon */}
                <div className="space-y-2">
                  <Label>Favicon</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center bg-gray-50">
                    {branding.favicon ? (
                      <img 
                        src={getLogoUrl(branding.favicon)} 
                        alt="Favicon" 
                        className="w-8 h-8 mx-auto mb-2"
                      />
                    ) : (
                      <div className="h-16 flex items-center justify-center text-gray-400">
                        Kein Favicon
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".png,.ico,.svg"
                        className="hidden"
                        onChange={(e) => handleLogoUpload(e, 'favicon')}
                      />
                      <Button variant="outline" size="sm" asChild>
                        <span><Upload className="h-4 w-4 mr-1" /> Hochladen</span>
                      </Button>
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">Browser-Tab Icon (32x32px)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Farben</CardTitle>
              <CardDescription>Primär- und Sekundärfarben für das System</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label>Primärfarbe</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={branding.primary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="w-12 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={branding.primary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                      placeholder="#1E40AF"
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Sekundärfarbe</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={branding.secondary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                      className="w-12 h-10 rounded border cursor-pointer"
                    />
                    <Input
                      value={branding.secondary_color}
                      onChange={(e) => setBranding(prev => ({ ...prev, secondary_color: e.target.value }))}
                      placeholder="#3B82F6"
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Color Preview */}
              <div className="mt-6 p-4 border rounded-lg">
                <p className="text-sm text-gray-500 mb-3">Vorschau:</p>
                <div className="flex gap-4 items-center">
                  <div 
                    className="w-24 h-10 rounded flex items-center justify-center text-white text-sm"
                    style={{ backgroundColor: branding.primary_color }}
                  >
                    Primär
                  </div>
                  <div 
                    className="w-24 h-10 rounded flex items-center justify-center text-white text-sm"
                    style={{ backgroundColor: branding.secondary_color }}
                  >
                    Sekundär
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleBrandingSave} disabled={saving} data-testid="save-branding-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Branding speichern
            </Button>
          </div>
        </TabsContent>

        {/* SYSTEM TEXTS TAB */}
        <TabsContent value="texts" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Konfigurierbare Texte</CardTitle>
              <CardDescription>
                Diese Texte werden Benutzern angezeigt. Verfügbare Platzhalter: 
                <code className="ml-2 px-1 py-0.5 bg-gray-100 rounded text-xs">
                  {'{{form_name}}'} {'{{department}}'} {'{{submitted_at}}'} {'{{status}}'}
                </code>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              
              {/* Submit Success */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">Nach erfolgreichem Absenden</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Deutsch</Label>
                    <Textarea
                      value={texts.submit_success_de}
                      onChange={(e) => setTexts(prev => ({ ...prev, submit_success_de: e.target.value }))}
                      placeholder="Vielen Dank! Ihr Formular wurde erfolgreich eingereicht."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Türkisch</Label>
                    <Textarea
                      value={texts.submit_success_tr}
                      onChange={(e) => setTexts(prev => ({ ...prev, submit_success_tr: e.target.value }))}
                      placeholder="Teşekkürler! Formunuz başarıyla gönderildi."
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Approved */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">Nach Genehmigung</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Deutsch</Label>
                    <Textarea
                      value={texts.approved_de}
                      onChange={(e) => setTexts(prev => ({ ...prev, approved_de: e.target.value }))}
                      placeholder="Ihr Formular {{form_name}} wurde genehmigt."
                      rows={3}
                    />
                    {texts.approved_de && (
                      <div className="mt-1 p-2 bg-emerald-50 rounded text-xs text-emerald-700">
                        <Eye className="h-3 w-3 inline mr-1" />
                        Vorschau: {previewText(texts.approved_de)}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Türkisch</Label>
                    <Textarea
                      value={texts.approved_tr}
                      onChange={(e) => setTexts(prev => ({ ...prev, approved_tr: e.target.value }))}
                      placeholder="{{form_name}} formunuz onaylandı."
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Rejected */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">Nach Ablehnung</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Deutsch</Label>
                    <Textarea
                      value={texts.rejected_de}
                      onChange={(e) => setTexts(prev => ({ ...prev, rejected_de: e.target.value }))}
                      placeholder="Ihr Formular {{form_name}} wurde leider abgelehnt."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Türkisch</Label>
                    <Textarea
                      value={texts.rejected_tr}
                      onChange={(e) => setTexts(prev => ({ ...prev, rejected_tr: e.target.value }))}
                      placeholder="{{form_name}} formunuz maalesef reddedildi."
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Completed */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">Nach Abschluss</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Deutsch</Label>
                    <Textarea
                      value={texts.completed_de}
                      onChange={(e) => setTexts(prev => ({ ...prev, completed_de: e.target.value }))}
                      placeholder="Vielen Dank für Ihre Einreichung zu {{form_name}}."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Türkisch</Label>
                    <Textarea
                      value={texts.completed_tr}
                      onChange={(e) => setTexts(prev => ({ ...prev, completed_tr: e.target.value }))}
                      placeholder="{{form_name}} için gönderiminiz için teşekkürler."
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Error Messages */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 border-b pb-2">Fehlermeldungen</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Deutsch</Label>
                    <Textarea
                      value={texts.error_generic_de}
                      onChange={(e) => setTexts(prev => ({ ...prev, error_generic_de: e.target.value }))}
                      placeholder="Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut."
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Türkisch</Label>
                    <Textarea
                      value={texts.error_generic_tr}
                      onChange={(e) => setTexts(prev => ({ ...prev, error_generic_tr: e.target.value }))}
                      placeholder="Bir hata oluştu. Lütfen tekrar deneyin."
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={handleTextsSave} disabled={saving} data-testid="save-texts-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Texte speichern
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BrandingSettings;
